/**
 * Shared hours-value selector over a typed Business profile's KB groups.
 *
 * Both the Overview glance card and the Free-test surface must agree on
 * which extracted value IS the opening hours, or the BDD scenario (the
 * glance shows hours, the Free test echoes the same hours) fails on a
 * disagreement that is invisible to a human.
 *
 * The fallback path emits a group literally named "Hours & availability"
 * with the hours as items[0], but the live Bedrock extract names groups
 * freely (e.g. "Contact & Hours") and may file the hours value under any
 * item key, with other items (phone, email) ahead of it. So we match
 * "hours" anywhere in an item key first, then fall back to the first
 * item of any group whose name mentions hours.
 *
 * Returns null when no hours fact was extracted at all, so callers can
 * render their own not-detected copy.
 */

import type { KbGroup } from "./types.js";

export function pickHoursValue(kbGroups: KbGroup[]): string | null {
  // First choice: any item whose key mentions hours, in any group. This
  // beats items[0] when the group merges phone/email ahead of hours.
  for (const g of kbGroups) {
    const byKey = g.items.find((it) => it.key.toLowerCase().includes("hours"));
    if (byKey?.value) return byKey.value;
  }
  // Second choice: the first item of any group whose name mentions hours.
  const hoursGroup = kbGroups.find((g) =>
    g.name.toLowerCase().includes("hours"),
  );
  const first = hoursGroup?.items[0];
  if (first?.value) return first.value;
  return null;
}
